import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from 'antd';
import { LuDownload } from 'react-icons/lu';
import SurveyReportApi from '../../../Api/SurveyReportApi';
import Loader from '../../ReusableComponents/Loader';
import BackButton from '../../ReusableComponents/BackButton';
import TmMasterTable from '../../ReusableComponents/TmMasterTable';
import TmFilter from '../../ReusableComponents/TmFilter';
import SearchInput from '../../ReusableComponents/SearchInput';
import { downloadSurveyUsersExcel } from '../../ReusableComponents/downloadSurveyExcel';
import { surveyBasedUsersHeader } from '../../../constant/tableHeader';
import { Modal, Checkbox } from 'antd';
import MysurveyQuestions from '../../MySurvey/MySurveyQuestions';

const filterArray = [
  {
    options: [
      { value: '', label: 'All' },
      { value: 'Completed', label: 'Completed' },
      { value: 'Not Completed', label: 'Not Completed' },
    ],
    optionFilterProp: 'label',
    placeholder: 'Completion Status',
    controlName: 'completionStatus',
    allowClear: true,
    input: 'select',
    col: 2,
  },
];

const TruncateText = ({ text }) => {
  const strText = String(text ?? '--');
  const LIMIT = 50;

  if (strText.length <= LIMIT) {
    return <span>{strText}</span>;
  }

  return (
    <span title={strText} style={{ cursor: 'help' }}>
      {`${strText.slice(0, LIMIT)}...`}
    </span>
  );
};

export const SurveyBasedUsersComp = () => {
  const { surveyId } = useParams();
  const debounceRef = useRef(null);
  const allRowsRef = useRef([]);
  const [loading, setLoading] = useState(false);
  const [displayRows, setDisplayRows] = useState([]);
  const [dynamicColumns, setDynamicColumns] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const [filterValue, setFilterValue] = useState({ completionStatus: '' });
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedRowIds, setSelectedRowIds] = useState([]);
  const decodedSurveyId = surveyId ? atob(surveyId) : '';
  const fetchSurveyUsers = React.useCallback(async () => {
    setLoading(true);
    try {
      const response = await SurveyReportApi.getSurveyUsers({
        survey_id: decodedSurveyId,
      });

      if (response?.data?.result) {
        const result = response.data.result || [];
        let rows = [];
        let allQuestions = [];
        result.forEach((u, index) => {
          let rowObj = {
            serialNo: index + 1,
            user_id: u.id,
            userName: u.userName || '--',
            employeeCode:
              u.employeeCode || u.employee_code || u.emp_code || '--',
            completion_status: u.status,
            completedDate: u.completedDate,
          };
          if (u.answer && Array.isArray(u.answer)) {
            const usedTitleCounts = {};

            u.answer.forEach((cat) => {
              if (cat.responses && Array.isArray(cat.responses)) {
                cat.responses.forEach((res) => {
                  let parsedAnswer = res.answer;
                  let isMatrix = false;
                  try {
                    if (
                      typeof parsedAnswer === 'string' &&
                      (parsedAnswer.startsWith('{') ||
                        parsedAnswer.startsWith('['))
                    ) {
                      parsedAnswer = JSON.parse(parsedAnswer);
                      if (
                        typeof parsedAnswer === 'object' &&
                        parsedAnswer !== null &&
                        !Array.isArray(parsedAnswer)
                      ) {
                        isMatrix = true;
                      }
                    }
                  } catch (e) {}
                  if (isMatrix) {
                    Object.keys(parsedAnswer).forEach((key) => {
                      const baseTitle = `${res.question} - ${key}`;
                      usedTitleCounts[baseTitle] =
                        (usedTitleCounts[baseTitle] || 0) + 1;
                      const colName =
                        usedTitleCounts[baseTitle] > 1
                          ? `${baseTitle} (${usedTitleCounts[baseTitle]})`
                          : baseTitle;

                      if (!allQuestions.includes(colName))
                        allQuestions.push(colName);
                      rowObj[colName] = parsedAnswer[key];
                    });
                  } else {
                    let displayAnswer = res.answer;
                    if (Array.isArray(parsedAnswer)) {
                      displayAnswer = parsedAnswer.join(', ');
                    }
                    const baseTitle = res.question;
                    usedTitleCounts[baseTitle] =
                      (usedTitleCounts[baseTitle] || 0) + 1;
                    const colName =
                      usedTitleCounts[baseTitle] > 1
                        ? `${baseTitle} (${usedTitleCounts[baseTitle]})`
                        : baseTitle;

                    if (!allQuestions.includes(colName))
                      allQuestions.push(colName);
                    rowObj[colName] = displayAnswer;
                  }
                });
              }
            });
          }
          rows.push(rowObj);
        });
        const newCols = [
          ...surveyBasedUsersHeader,
          ...allQuestions.map((q) => ({
            field: q,
            headerName: q,
            render: (text) => <TruncateText text={text} />,
          })),
        ];

        allRowsRef.current = rows;
        setDisplayRows(rows);
        setDynamicColumns(newCols);
      } else {
        setDisplayRows([]);
        allRowsRef.current = [];
      }
    } catch (error) {
      console.error('SurveyBasedUsersComp Error', error);
    } finally {
      setLoading(false);
    }
  }, [decodedSurveyId]);

  useEffect(() => {
    fetchSurveyUsers();
  }, [fetchSurveyUsers]);

  const applyFilters = (search, filters) => {
    let result = allRowsRef.current;
    if (search && search.trim()) {
      const lower = search.trim().toLowerCase();
      result = result.filter((r) =>
        (r.userName || '').toLowerCase().includes(lower),
      );
    }
    const status = filters?.completionStatus || '';
    if (status) {
      result = result.filter((r) => {
        const rowStatus = r.completion_status || '';
        return rowStatus.toLowerCase() === status.toLowerCase();
      });
    }
    setDisplayRows(result.map((r, idx) => ({ ...r, serialNo: idx + 1 })));
    // Optional: reset selected row ids when filter changes, or keep them.
    // We'll keep them to allow cross-filter selection, but it's up to preference.
  };
  const handleSearchChange = (value) => {
    setSearchValue(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(
      () => applyFilters(value, filterValue),
      300,
    );
  };
  const handleFilterChange = (obj) => {
    const updated = { ...filterValue, ...obj };
    setFilterValue(updated);
    applyFilters(searchValue, updated);
  };
  const toggleModal = (action, row) => {
    if (action !== 'View') return;
    if (!row?.user_id) return;
    setSelectedUser(row);
    setModalOpen(true);
  };

  const handleDownload = async () => {
    let rowsToDownload = displayRows;
    if (selectedRowIds.length > 0) {
      rowsToDownload = displayRows.filter((r) =>
        selectedRowIds.includes(r.user_id)
      );
    }
    let analyticsData = [];
    try {
      const response = await SurveyReportApi.getSurveyBasedAnalytics({
        survey_id: decodedSurveyId,
      });
      if (response?.data?.result) {
        analyticsData = response.data.result;
      }
    } catch (error) {
      console.error('Error fetching analytics data', error);
    }

    await downloadSurveyUsersExcel(
      rowsToDownload,
      dynamicColumns,
      'Survey_Users.xlsx',
      analyticsData
    );
  };

  return (
    <div>
      <div className="d-md-flex align-items-center mb-2">
        <div className="d-flex align-items-center me-auto">
          <BackButton />
          <h5 className="mb-0 ms-2">Survey Attend Reports</h5>
        </div>

        <div className="d-flex align-items-center gap-2 mt-2 mt-md-0">
          <SearchInput
            placeholder="Search user"
            value={searchValue}
            onSearchChange={handleSearchChange}
          />
          <Button
            type="dashed"
            htmlType="button"
            onClick={handleDownload}
            icon={<LuDownload size={16} />}
          >
            Download
          </Button>
        </div>
      </div>
      <TmFilter
        filterArray={filterArray}
        handleFilterChange={handleFilterChange}
      />

      {loading ? (
        <Loader />
      ) : (
        <TmMasterTable
          bordered={true}
          rows={displayRows}
          columns={[
            {
              field: 'checkbox',
              headerName: (
                <div onClick={(e) => e.stopPropagation()}>
                  <Checkbox
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedRowIds(displayRows.map((r) => r.user_id));
                      } else {
                        setSelectedRowIds([]);
                      }
                    }}
                    checked={
                      displayRows.length > 0 &&
                      selectedRowIds.length === displayRows.length
                    }
                  />
                </div>
              ),
              align: 'center',
              render: (text, row) => (
                <div onClick={(e) => e.stopPropagation()}>
                  <Checkbox
                    checked={selectedRowIds.includes(row.user_id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedRowIds((prev) => [...prev, row.user_id]);
                      } else {
                        setSelectedRowIds((prev) =>
                          prev.filter((id) => id !== row.user_id)
                        );
                      }
                    }}
                  />
                </div>
              ),
            },
            ...dynamicColumns,
          ]}
          custom={['action']}
          toggleModal={toggleModal}
          buttonPermission={{ view: true, edit: false, delete: false }}
        />
      )}

      <Modal
        title={selectedUser ? `Survey Answers - ${selectedUser.userName}` : ''}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        footer={null}
        width={1100}
        destroyOnClose
        style={{ top: 20 }}
      >
        <div
          style={{ maxHeight: '80vh', overflowY: 'auto', overflowX: 'hidden' }}
        >
          {selectedUser && (
            <MysurveyQuestions
              isModal={true}
              modalSurveyId={decodedSurveyId}
              modalUserId={selectedUser.user_id}
              isCompleted={true}
              onCancel={() => setModalOpen(false)}
            />
          )}
        </div>
      </Modal>
    </div>
  );
};
